import { useEffect, useMemo, useState } from "react";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import {
  ExternalLink, Loader2, Sparkles, X,
} from "lucide-react";
import { api, formatError } from "../lib/api";
import { CopyBtn } from "./CopyBtn";

const PLATFORMS = [
  { id: "tiktok", label: "TikTok", deepLink: "https://www.tiktok.com/upload", color: "bg-black" },
  { id: "instagram", label: "Instagram", deepLink: "https://www.instagram.com/", color: "bg-gradient-to-br from-pink-500 via-red-500 to-yellow-400" },
  { id: "youtube", label: "YouTube", deepLink: "https://studio.youtube.com/", color: "bg-red-600" },
  { id: "twitter", label: "Twitter/X", deepLink: "https://twitter.com/compose/tweet", color: "bg-zinc-950" },
  { id: "linkedin", label: "LinkedIn", deepLink: "https://www.linkedin.com/feed/?shareActive=true", color: "bg-[#0077b5]" },
];

/**
 * PublishModal — one-click publish helper.
 * Takes a script/content and auto-generates matching hashtags + caption variants, then
 * offers per-platform copy-to-clipboard + deep links into the platform's native composer.
 */
export function PublishModal({ content, topic, onClose }) {
  const [platform, setPlatform] = useState("tiktok");
  const [loading, setLoading] = useState(true);
  const [hashtags, setHashtags] = useState([]);
  const [captions, setCaptions] = useState([]);
  const [err, setErr] = useState("");
  const [selectedCaption, setSelectedCaption] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [hRes, cRes] = await Promise.all([
          api.post("/social/hashtags", { topic: topic || content.slice(0, 150), platform, count: 15 }),
          api.post("/social/captions", { topic: topic || content.slice(0, 200), platform, variants: 3 }),
        ]);
        if (cancelled) return;
        setHashtags(hRes.data.hashtags || []);
        setCaptions(cRes.data.captions || []);
        setSelectedCaption(0);
      } catch (error) {
        if (cancelled) return;
        logger.error("Publish kit error", error);
        setErr(formatError(error.response?.data?.detail) || "Failed to build kit");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [platform, content, topic]);

  const platformMeta = useMemo(() => PLATFORMS.find((p) => p.id === platform) || PLATFORMS[0], [platform]);
  const activeCaption = captions[selectedCaption] || "";
  const bundle = useMemo(() => {
    const tags = hashtags.join(" ");
    return `${activeCaption}\n\n${tags}`.trim();
  }, [activeCaption, hashtags]);

  const handleOpenPlatform = async () => {
    try {
      await navigator.clipboard.writeText(bundle);
      toast.success(`Copied! Opening ${platformMeta.label}...`);
    } catch (error) {
      logger.error("Clipboard on publish", error);
    }
    window.open(platformMeta.deepLink, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="publish-modal">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-auto shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-5 py-4 sticky top-0 bg-white dark:bg-zinc-900 z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand" />
            <h3 className="font-bold text-zinc-900 dark:text-white font-heading">One-Click Publish Kit</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white" data-testid="close-publish-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="text-xs font-bold tracking-wider text-zinc-400 uppercase block mb-2">Platform</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  data-testid={`publish-platform-${p.id}`}
                  className={`text-xs font-semibold px-3 py-2 border transition-all ${platform === p.id ? "border-brand bg-brand/5 text-brand" : "border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-zinc-400"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-10 gap-2" data-testid="publish-loading">
              <Loader2 className="w-8 h-8 animate-spin text-brand" />
              <p className="text-sm text-zinc-500">Building your {platformMeta.label} kit...</p>
            </div>
          )}

          {!loading && err && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3" data-testid="publish-error">{err}</div>
          )}

          {!loading && !err && (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold tracking-wider text-zinc-400 uppercase">Pick a caption</label>
                  <span className="text-xs text-zinc-400">{captions.length} variants</span>
                </div>
                <div className="space-y-2">
                  {captions.map((c, i) => (
                    <button
                      key={c.slice(0, 40) + i}
                      onClick={() => setSelectedCaption(i)}
                      data-testid={`publish-caption-${i}`}
                      className={`w-full text-left p-3 border transition-all ${selectedCaption === i ? "border-brand bg-brand/5" : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-400"}`}
                    >
                      <span className="text-[10px] font-bold tracking-wider text-brand uppercase">Variant {i + 1}</span>
                      <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-1 whitespace-pre-wrap">{c}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold tracking-wider text-zinc-400 uppercase">Hashtags</label>
                  <CopyBtn text={hashtags.join(" ")} label="Copy tags" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {hashtags.map((h) => (
                    <span key={h} className="text-xs font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 px-2 py-1">{h}</span>
                  ))}
                </div>
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold tracking-wider text-zinc-400 uppercase">Final bundle (caption + hashtags)</label>
                  <CopyBtn text={bundle} label="Copy all" />
                </div>
                <pre className="text-xs text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap font-sans max-h-40 overflow-auto" data-testid="publish-bundle-preview">{bundle}</pre>
              </div>

              <button
                onClick={handleOpenPlatform}
                data-testid="publish-open-platform-btn"
                className={`w-full py-3.5 text-sm font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-all ${platformMeta.color}`}
              >
                Copy & open {platformMeta.label} <ExternalLink className="w-4 h-4" />
              </button>
              <p className="text-xs text-zinc-400 text-center">Your caption + hashtags are copied to clipboard. Paste into {platformMeta.label}'s composer.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
